import {
  CUSTOMER_FEE_TIERS_SPACE_PARAMETER_SUPPORT,
  useDealerInfluencedEntityAdminSpaceParameters,
} from '@src/components/AppLayout/Panels/Dealer/DealerSpaceParameters';
import { EntityAdmin, type FeeTier } from '@talos/kyoko';
import type { useCustomerPricingTiersShared } from './useCustomerPricingTiersShared';
import {
  CUSTOMER_FEE_TIERS_PERSIST_KEY,
  CUSTOMER_PRICING_FEE_TIERS_TAB,
  CUSTOMER_PRICING_TIERS_FEE_TIERS_BULK_EDIT_FIELDS,
  CUSTOMER_PRICING_TIERS_FEE_TIERS_COLUMNS,
  CUSTOMER_PRICING_TIERS_GROUP_COLUMN_DEF,
  getCustomerPricingTiersFeeTierPatchDeletePath,
  getCustomerPricingTiersFeeTierPostPath,
} from './utils';

export type CustomerFeeTiersProps = {
  shared: ReturnType<typeof useCustomerPricingTiersShared>;
  persistKey?: string;
};

/** Standalone Fee Tiers EntityAdmin — used by the Pricing Tiers page and Spaces panels. */
export function CustomerFeeTiers({ shared, persistKey = CUSTOMER_FEE_TIERS_PERSIST_KEY }: CustomerFeeTiersProps) {
  const {
    canEditDealer,
    maxBulkEditRows,
    globalDefault,
    getFeeTiersDrawerOptions,
    feeTiersFilterableProperties,
    defaultPricingRuleAction,
    getChildEntityDisplayName,
  } = shared;
  const spaceParameterProps = useDealerInfluencedEntityAdminSpaceParameters(
    CUSTOMER_FEE_TIERS_SPACE_PARAMETER_SUPPORT,
    feeTiersFilterableProperties
  );

  return (
    <EntityAdmin<FeeTier>
      title={CUSTOMER_PRICING_FEE_TIERS_TAB.label}
      subtitle="Set up your Fee Tiers here."
      path="/fee-tiers"
      queryParams={{ expanded: 'true' }}
      globalEntity={globalDefault}
      getPostPath={getCustomerPricingTiersFeeTierPostPath}
      getPatchPath={getCustomerPricingTiersFeeTierPatchDeletePath}
      columns={CUSTOMER_PRICING_TIERS_FEE_TIERS_COLUMNS}
      entityIDField="Tier"
      childIDField="Symbol"
      childEntityName="Symbol Override"
      getChildEntityDisplayName={getChildEntityDisplayName}
      getEntityDrawerOptions={getFeeTiersDrawerOptions}
      {...spaceParameterProps}
      entityName="Fee Tier"
      allowAddEntity={canEditDealer}
      allowEditEntity={canEditDealer}
      allowDeleteEntity={canEditDealer}
      groupColumnDef={CUSTOMER_PRICING_TIERS_GROUP_COLUMN_DEF}
      persistKey={persistKey}
      useTabs={false}
      panelActions={[defaultPricingRuleAction]}
      allowBulkEdit={true}
      bulkEditFields={CUSTOMER_PRICING_TIERS_FEE_TIERS_BULK_EDIT_FIELDS}
      maxBulkEditRows={maxBulkEditRows}
    />
  );
}
